import { useMemo, useState } from 'react';
import { useAdCampaignsDataContext } from 'src/contexts/AdCampaignsDataContext';
import { Content } from 'src/contexts/ContentContext/Content';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  CampaignsTableV1,
  CampaignsTableV2,
  PastFlightsTable,
} from 'src/tables/SponsoredListings';
import { ContentId } from 'src/utils/constants/contentId';
import {
  AdCampaignState,
  Event,
  Feature,
  SponsoredListingsCampaign,
  SponsoredListingsFlight,
} from 'src/WebApiController';

import * as styles from '../InventoryEventPage.css';

export const InventoryEventBoostSection = ({ event }: { event?: Event }) => {
  const [isLoading, setIsLoading] = useState(() => false);

  const { adCampaignsDataQuery, allCampaignData, refreshData } =
    useAdCampaignsDataContext();

  const hasAdCampaignManagerFeature = useUserHasFeature(
    Feature.SponsoredListingsV2_CampaignManager
  );

  const allCampaigns = useMemo(() => {
    if (event?.viagId == null || !allCampaignData) {
      return null;
    }
    return allCampaignData[event?.viagId] ?? [];
  }, [allCampaignData, event?.viagId]);

  const activeCampaigns = useMemo(() => {
    if (allCampaigns == null) {
      return null;
    }
    return allCampaigns.filter(
      (c) => c.campaignState == AdCampaignState.Active && !c.hasEnded
    );
  }, [allCampaigns]);

  const inactiveCampaigns = useMemo(() => {
    if (allCampaigns == null) {
      return null;
    }
    return allCampaigns.filter(
      (c) => c.campaignState == AdCampaignState.Paused || c.hasEnded
    );
  }, [allCampaigns]);

  const campaignsWithPastFlights = useMemo(() => {
    if (allCampaigns == null) {
      return null;
    }

    const currentFlight = allCampaigns
      .flatMap((c) => c.flights ?? [])
      .find((f) => f.isCurrent);

    const dateThreshold = currentFlight?.dateRange.start
      ? new Date(currentFlight.dateRange.start)
      : new Date();

    return allCampaigns
      .map((c) => ({
        campaign: c,
        flights: c.flights?.filter(
          (flight) =>
            flight.dateRange.end &&
            new Date(flight.dateRange.end) < dateThreshold
        ),
      }))
      .filter((cf) => cf.flights?.length)
      .flatMap((cf) =>
        cf.flights!.map((f) => cf.campaign && [cf.campaign, f])
      ) as [SponsoredListingsCampaign, SponsoredListingsFlight][];
  }, [allCampaigns]);

  if (event?.viagId == null) {
    return null;
  }

  return (
    <div className={styles.tableSection}>
      {hasAdCampaignManagerFeature ? (
        <>
          <Stack
            gap="m"
            direction="column"
            width="full"
            style={{ height: activeCampaigns?.length ? '50%' : 'fit-content' }}
          >
            <span>
              <Content id={ContentId.ActiveCampaigns} /> (
              {activeCampaigns?.length ?? 0})
            </span>
            <CampaignsTableV2
              disabled={isLoading}
              isLoading={adCampaignsDataQuery.isLoading}
              campaigns={activeCampaigns}
              onStartWork={() => setIsLoading(true)}
              onStopWork={(success) => {
                setIsLoading(false);
                if (success) refreshData();
              }}
              hideActive={false}
              hidePrevious={false}
              hideOlder={false}
            />
          </Stack>
          <Stack
            gap="m"
            direction="column"
            width="full"
            style={{
              height: inactiveCampaigns?.length ? '50%' : 'fit-content',
            }}
          >
            <span>
              <Content id={ContentId.InactiveCampaigns} /> (
              {inactiveCampaigns?.length ?? 0})
            </span>
            <CampaignsTableV2
              disabled={isLoading}
              isLoading={adCampaignsDataQuery.isLoading}
              campaigns={inactiveCampaigns}
              onStartWork={() => setIsLoading(true)}
              onStopWork={(success) => {
                setIsLoading(false);
                if (success) refreshData();
              }}
              hideActive={false}
              hidePrevious={false}
              hideOlder={false}
            />
          </Stack>
        </>
      ) : (
        <>
          <Stack
            gap="m"
            direction="column"
            width="full"
            style={{ height: activeCampaigns?.length ? '25%' : 'fit' }}
          >
            <span>
              <Content id={ContentId.ActiveCampaigns} /> (
              {activeCampaigns?.length ?? 0})
            </span>
            <CampaignsTableV1
              disabled={isLoading}
              isLoading={adCampaignsDataQuery.isLoading}
              campaigns={activeCampaigns}
              onStartWork={() => setIsLoading(true)}
              onStopWork={(success) => {
                setIsLoading(false);
                if (success) refreshData();
              }}
            />
          </Stack>
          <Stack
            gap="m"
            direction="column"
            width="full"
            style={{ height: inactiveCampaigns?.length ? '25%' : 'fit' }}
          >
            <span>
              <Content id={ContentId.InactiveCampaigns} /> (
              {inactiveCampaigns?.length ?? 0})
            </span>
            <CampaignsTableV1
              disabled={isLoading}
              isLoading={adCampaignsDataQuery.isLoading}
              campaigns={inactiveCampaigns}
              onStartWork={() => setIsLoading(true)}
              onStopWork={(success) => {
                setIsLoading(false);
                if (success) refreshData();
              }}
            />
          </Stack>
          <Stack
            gap="m"
            direction="column"
            width="full"
            style={{
              height: campaignsWithPastFlights?.length ? '50%' : 'fit',
            }}
          >
            <span>
              <Content id={ContentId.HistoricPerformance} /> (
              {campaignsWithPastFlights?.length ?? 0})
            </span>
            <PastFlightsTable
              isLoading={adCampaignsDataQuery.isLoading}
              campaignsAndFlights={campaignsWithPastFlights}
            />
          </Stack>
        </>
      )}
    </div>
  );
};
